import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { Mail, MapPin, Phone } from 'lucide-react';
import { API } from '@/App';
import axios from 'axios';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success('Thank you for contacting us! We\'ll get back to you soon.');
      // Reset form
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Failed to submit contact form:', error);
      toast.error('Failed to send message. Please email us directly at info@herenterprisehub.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
      <Navigation />
      
      <div className="container mx-auto px-4 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
            Get in Touch
          </h1>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <Card className="glass">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                    Contact Information
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Email */}
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-rose-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Email Us</p>
                        <a href="mailto:info@herenterprisehub.com" className="text-lg text-rose-600 hover:text-rose-700 font-semibold">
                          info@herenterprisehub.com
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8 pt-8 border-t border-rose-200">
                    <h4 className="font-semibold mb-3 text-gray-800">Business Hours</h4>
                    <p className="text-gray-600 text-sm mb-2">Monday - Friday: 9:00 AM - 6:00 PM CET</p>
                    <p className="text-gray-600 text-sm">We typically respond within 24 hours during business days.</p>
                  </div>
                </CardContent>
              </Card>

              {/* Why Contact Us */}
              <Card className="glass">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                    How Can We Help?
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-rose-600">•</span>
                      <span>General inquiries about our platform</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-rose-600">•</span>
                      <span>Partnership opportunities</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-rose-600">•</span>
                      <span>Technical support</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-rose-600">•</span>
                      <span>Membership questions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-rose-600">•</span>
                      <span>Media and press inquiries</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Send Us a Message
                </CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you shortly.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="h-12"
                      required
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      placeholder="Your Email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="h-12"
                      required
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="h-12"
                      required
                    />
                  </div>
                  <div>
                    <Textarea
                      placeholder="Your Message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={6}
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-rose-600 hover:bg-rose-700 text-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                    Or email us directly at info@herenterprisehub.com
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ContactUs;
